<script lang="ts">
  import { Component, Prop, Vue } from 'vue-property-decorator';
  import { Globals } from '@/modules/Globals';
  import { vueData } from '@/VueData';
  import { IOrderItem } from '@/interfaces/IOrderItem';
  import {
    applyRound,
    bitCount,
    coordsInsideZone,
    displayPrice,
    getDisplayOptions,
    round2,
  } from '@/utils';
  import { OrderType } from '@/enums/OrderEnums';
  import { TaxDisplayMode } from '@/enums/TaxDisplayMode';
  import DisplayValue from '@/components/DisplayValue.vue';

  interface IOrderData {
    type: OrderType;
    tel: string | null;
    whatsapp: string | null;
    name: string | null;
    comment: string | null;
  }

  interface ITotals {
    totalPrice: number;
    totalCount: number;
    taxAmount: number;
    subTotal: number;
  }

  @Component({
    components: { DisplayValue },
  })
  export default class NewOrderAddedItems extends Vue {
    @Prop() orderItems: IOrderItem[];
    @Prop() orderData: IOrderData;
    @Prop() deliveryFee: null | number;
    @Prop() totals: ITotals;
    uploadsPath = Globals.uploadsPath;
    displayPrice = displayPrice;

    get currency() {
      return vueData.currency;
    }

    get restaurant() {
      return vueData.restaurant;
    }

    get restaurantSettings() {
      return vueData.restaurantSettings;
    }

    get optionsByIndex() {
      return getDisplayOptions(this.orderItems);
    }

    get hasTax() {
      return (
        this.restaurantSettings.taxMode !== TaxDisplayMode.NoTax &&
        this.restaurant.tax
      );
    }
  }
</script>

<template>
  <div class="order-items-container">
    <div class="d-flex flex-row justify-space-between align-center">
      <h3 class="black--text font-weight-medium">Items del pedido</h3>
    </div>
    <p class="font-size-9e grey--text mb-0" v-if="!orderItems.length">
      Presione el boton para agregar items
    </p>
    <template v-else>
      <div class="order-items">
        <v-row
          align="center"
          class="order-item flex-nowrap"
          v-for="(orderItem, index) of orderItems"
          :key="index"
        >
          <v-col cols="auto">
            <span class="amount">{{ orderItem.amount }}x</span>
          </v-col>
          <v-col cols="auto" class="px-0" v-if="orderItem.item.picture">
            <v-img
              class="picture"
              :src="`${uploadsPath}/${orderItem.item.picture}`"
            ></v-img>
          </v-col>
          <v-col class="meta flex-fill">
            <h4 class="name">{{ orderItem.item.name }}</h4>
            <div class="options" v-if="optionsByIndex[orderItem.index]">
              <div
                v-for="(dO, index) of optionsByIndex[orderItem.index]"
                :key="index"
              >
                <template
                  v-if="
                    dO.option.type === 'choose' && dO.optionItems.length === 1
                  "
                >
                  <display-value
                    class="my-0 options__item"
                    :title="
                      dO.option.title +
                      ': ' +
                      dO.optionItems[0].optionItem.title
                    "
                    :client-bold="false"
                    :space-between="true"
                  >
                    <span class="font-weight-medium"
                      >{{ dO.optionItems[0].total > 0 ? currency.symbol : ''
                      }}{{
                        dO.optionItems[0].total > 0
                          ? dO.optionItems[0].total
                          : '-'
                      }}</span
                    >
                  </display-value>
                </template>
                <template v-else>
                  <h4>{{ dO.option.title }}</h4>
                  <display-value
                    class="my-0 options__item"
                    v-for="option of dO.optionItems"
                    :key="option.optionItemId"
                    :title="
                      ' - ' +
                      (option.optionItem.option.type === 'add' || option.val > 1
                        ? `${option.val}x `
                        : '') +
                      option.optionItem.title
                    "
                    :client-bold="false"
                    :space-between="true"
                  >
                    <span class="font-weight-medium"
                      >{{ option.total > 0 ? currency.symbol : ''
                      }}{{ option.total > 0 ? option.total : '-' }}</span
                    >
                  </display-value>
                </template>
              </div>
            </div>

            <div>
              <span class="float-left"
                >{{ currency.symbol }}{{ orderItem.price }}</span
              >
              <span class="float-right font-weight-bold"
                >{{ currency.symbol }}{{ orderItem.total }}</span
              >
            </div>
            <div class="clear"></div>
            <display-value
              class="comments"
              :title="$t('order.comments')"
              v-if="orderItem.comment"
            >
              <p class="mb-0">{{ orderItem.comment }}</p>
            </display-value>
          </v-col>
          <v-col cols="auto">
            <v-btn small color="info" icon @click="$emit('edit', orderItem)">
              <v-icon small>mdi-pencil</v-icon>
            </v-btn>
            <v-btn small color="grey" icon @click="$emit('remove', orderItem)">
              <v-icon small>mdi-delete</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </div>
      <div class="order-items-footer">
        <display-value
          v-if="
            orderData.type === 'delivery' &&
            (!hasTax || restaurantSettings.taxDelivery)
          "
          title="Costo de envío"
          :client-bold="false"
          space-between
          >{{
            deliveryFee === null
              ? 'A definir'
              : deliveryFee === 0
              ? 'Gratis'
              : `${
                  currency.symbol +
                  displayPrice(deliveryFee, restaurantSettings.showDecimals)
                }`
          }}</display-value
        >
        <display-value title="Subtotal" :client-bold="false" space-between
          >{{ currency.symbol
          }}{{
            displayPrice(totals.subTotal, restaurantSettings.showDecimals)
          }}</display-value
        >
        <display-value
          v-if="hasTax && restaurantSettings.taxMode === 3"
          :title="`${restaurant.taxName} (${restaurant.tax}%)`"
          :client-bold="false"
          space-between
          >{{ currency.symbol
          }}{{
            displayPrice(totals.taxAmount, restaurantSettings.showDecimals)
          }}</display-value
        >
        <display-value
          v-if="
            orderData.type === 'delivery' &&
            hasTax &&
            !restaurantSettings.taxDelivery
          "
          title="Costo de envío"
          :client-bold="false"
          space-between
          >{{
            deliveryFee === null
              ? 'A definir'
              : deliveryFee === 0
              ? 'Gratis'
              : `${
                  currency.symbol +
                  displayPrice(deliveryFee, restaurantSettings.showDecimals)
                }`
          }}</display-value
        >
        <display-value title="Total" space-between
          >{{ currency.symbol
          }}{{
            displayPrice(totals.totalPrice, restaurantSettings.showDecimals)
          }}</display-value
        >
      </div>
    </template>
  </div>
</template>

<style scoped lang="scss"></style>
